<template>
  <form class="create-or-edit-skill-form" @submit.prevent.stop="submit">
    <div class="form-content">
      <div class="form-row">
        <p-text-field
          v-model="localItem.displayDescription"
          :disabled="!$hasPermission('skills')"
          :label="'Display Name'"
          :placeholder="'Optional — brief label shown to users in the chat interface'"
          :maxlength="120"
        />
        <div class="char-counter" :class="{ warn: displayDescriptionLength > 100 }">{{ displayDescriptionLength }} / 120</div>
      </div>
      <div class="form-row">
        <label class="field-label">
          SKILL.md
          <span
            class="info-tooltip"
            title="Full SKILL.md file including YAML frontmatter. The name and description fields in the frontmatter are read by the model."
            >?</span
          >
        </label>
        <div class="markdown-editor">
          <Code
            :key="contentKey"
            v-model="localItem.content"
            :read-only="!$hasPermission('skills')"
            language="markdown"
            :auto-height="false"
            class="code-editor"
          />
        </div>
        <div v-if="contentError" class="error content-error">{{ contentError }}</div>
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.tools"
          :options="toolOptions"
          :disabled="!$hasPermission('skills')"
          label="Allowed Tools (optional — tools available when this skill is active)"
          placeholder="Select tools (optional)"
          :searchable="true"
          :multiple="true"
          open-direction="top"
        />
      </div>
      <div class="form-row">
        <label class="field-label">Files</label>
        <SkillFileTree
          :skill-name="localItem.name"
          :initial-files="initialFiles"
          :disabled="!$hasPermission('skills') || !!previewVersion"
          :version="previewVersion"
          @change="onFileTreeChange"
        />
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.organizations"
          :options="organizationsOptions"
          :get-label-callback="getOrganizationName"
          :disabled="!$hasPermission('skills')"
          label="Organizations (optional — restrict to specific organizations)"
          placeholder="Select organizations (optional)"
          :searchable="true"
          :multiple="true"
          open-direction="top"
        />
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.models"
          :options="modelOptions"
          :get-label-callback="getModelLabel"
          :disabled="!$hasPermission('skills')"
          label="Models (optional — restrict to specific models)"
          placeholder="Select models (optional)"
          :searchable="true"
          :multiple="true"
          open-direction="top"
        />
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.tags"
          :multiple="true"
          :disabled="!$hasPermission('skills')"
          :taggable="true"
          label="Tags"
          placeholder="Add tags (optional)"
          tag-placeholder=""
          open-direction="top"
          @tag="tagAdded"
        />
      </div>
      <div v-if="isRequestFailed" class="error">Failed to save skill. Check your input and try again.</div>
    </div>
    <div class="button-container">
      <p-button type="button" @click.prevent="onCancel">Cancel</p-button>
      <p-button v-if="$hasPermission('skills')" color="primary" type="submit" :disabled="isRequestPending || isInvalid || !isDirty">Save</p-button>
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex';

import Multiselect from '@/components/common/Multiselect';
import TextField from '@/components/common/TextField';
import Button from '@/components/common/Button';
import Code from '@/components/common/Code';
import SkillFileTree from './SkillFileTree';

function validateSkillContent(content) {
  if (!content || !content.trim()) return 'SKILL.md must not be empty.';
  const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch) return 'SKILL.md must start with a YAML frontmatter block (--- ... ---).';
  if (!/^name:\s*.+/m.test(fmMatch[1])) return 'Frontmatter must include a name field.';
  const nameMatch = fmMatch[1].match(/^name:\s*(.+)/m);
  const name = nameMatch?.[1]?.trim();
  if (name) {
    if (name.length > 64) return 'Skill name must be 64 characters or fewer.';
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name))
      return 'Skill name must contain only lowercase letters, numbers, and hyphens, and must not start or end with a hyphen.';
  }
  if (!/^description:\s*.+/m.test(fmMatch[1])) return 'Frontmatter must include a description field.';
  return null;
}

function buildTemplate() {
  return `---\nname: my-skill-name\ndescription: Describe when and why the model should use this skill.\n---\n\n## Instructions\n<!-- Step-by-step rules and workflows the model must follow. -->\n\n## Example Usage\n<!-- Example prompts that would trigger this skill. -->\n`;
}

export default {
  components: {
    'p-text-field': TextField,
    'p-button': Button,
    Code,
    Multiselect,
    SkillFileTree
  },
  props: {
    item: {
      type: Object,
      default: () => null
    },
    isRequestPending: {
      type: Boolean,
      default: false
    },
    isRequestFailed: {
      type: Boolean,
      default: false
    },
    initialFiles: {
      type: Array,
      default: () => []
    },
    previewVersion: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      localItem: this.item
        ? { ...this.item, tools: this.extractToolsFromContent(this.item.content) }
        : {
            displayDescription: '',
            content: buildTemplate(),
            tags: [],
            organizations: [],
            models: [],
            tools: []
          },
      contentKey: 0,
      contentError: null,
      stagedFiles: [],
      newFolders: [],
      deletePaths: [],
      syncGuard: false,
      originalSnapshot: this.item
        ? JSON.stringify({
            displayDescription: this.item.displayDescription || '',
            content: this.item.content || '',
            tags: this.item.tags || [],
            organizations: this.item.organizations || [],
            models: this.item.models || []
          })
        : null
    };
  },
  computed: {
    ...mapState({
      models: s => s.nlp.models,
      organizationsCollection: s => s.organizations.collection,
      tools: s => s.nlp.tools
    }),
    organizationsOptions() {
      return this.organizationsCollection.map(o => o.id) ?? [];
    },
    isEditing() {
      return !!this.item;
    },
    modelOptions() {
      return this.models.map(m => m.id) || [];
    },
    toolOptions() {
      return this.tools.map(t => t.name) || [];
    },
    displayDescriptionLength() {
      return (this.localItem.displayDescription || '').length;
    },
    isInvalid() {
      return validateSkillContent(this.localItem.content) !== null;
    },
    isDirty() {
      if (!this.isEditing) return true;
      if (this.stagedFiles.length || this.newFolders.length || this.deletePaths.length) return true;
      const current = JSON.stringify({
        displayDescription: this.localItem.displayDescription || '',
        content: this.localItem.content || '',
        tags: this.localItem.tags || [],
        organizations: this.localItem.organizations || [],
        models: this.localItem.models || []
      });
      return current !== this.originalSnapshot;
    }
  },
  watch: {
    item: {
      handler(newItem) {
        if (newItem) {
          this.localItem = { ...newItem, tools: this.extractToolsFromContent(newItem.content) };
          this.contentKey++;
          this.originalSnapshot = JSON.stringify({
            displayDescription: newItem.displayDescription || '',
            content: newItem.content || '',
            tags: newItem.tags || [],
            organizations: newItem.organizations || [],
            models: newItem.models || []
          });
          this.stagedFiles = [];
          this.newFolders = [];
          this.deletePaths = [];
        }
      },
      deep: true
    },
    'localItem.tools': {
      handler(newTools) {
        if (this.syncGuard) return;
        this.syncGuard = true;
        this.localItem.content = this.updateToolsInContent(this.localItem.content, newTools || []);
        this.contentKey++;
        this.$nextTick(() => {
          this.syncGuard = false;
        });
      },
      deep: true
    },
    'localItem.content': {
      handler(newContent) {
        if (this.syncGuard) return;
        this.syncGuard = true;
        const tools = this.extractToolsFromContent(newContent);
        if (JSON.stringify(tools) !== JSON.stringify(this.localItem.tools)) {
          this.localItem.tools = tools;
        }
        this.$nextTick(() => {
          this.syncGuard = false;
        });
      }
    }
  },
  async created() {
    await Promise.all([this.$store.dispatch('nlp/getConfigurations'), this.$store.dispatch('organizations/getCollection')]);
  },
  methods: {
    getOrganizationName(value) {
      return this.organizationsCollection.find(o => o.id === value)?.name ?? 'Unknown Organization';
    },
    getModelLabel(id) {
      return this.models.find(m => m.id === id)?.name ?? id;
    },
    extractToolsFromContent(content) {
      if (!content) return [];
      const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
      if (!fmMatch) return [];
      const toolsMatch = fmMatch[1].match(/^tools:\s*\n((?:\s+-\s+.+\n?)*)/m);
      if (!toolsMatch) return [];
      return toolsMatch[1]
        .split('\n')
        .map(line => line.replace(/^\s+-\s+/, '').trim())
        .filter(Boolean);
    },
    updateToolsInContent(content, tools) {
      if (!content) return content;
      const fmMatch = content.match(/^(---\n)([\s\S]*?)(\n---)/);
      if (!fmMatch) return content;
      let fm = fmMatch[2];
      // Remove existing tools block
      fm = fm.replace(/\ntools:\s*\n(?:\s+-\s+.+\n?)*/m, '');
      fm = fm.replace(/\ntools:\s*\[\]\s*/m, '');
      // Add tools block if there are tools
      if (tools.length) {
        fm += '\ntools:\n' + tools.map(t => '  - ' + t).join('\n');
      }
      return fmMatch[1] + fm + fmMatch[3] + content.slice(fmMatch[0].length);
    },
    tagAdded(tag) {
      if (!Array.isArray(this.localItem.tags)) {
        this.localItem.tags = [];
      }
      if (this.localItem.tags.includes(tag)) {
        return;
      }
      this.localItem.tags.push(tag);
    },
    onFileTreeChange({ stagedFiles, newFolders, deletePaths }) {
      this.stagedFiles = stagedFiles;
      this.newFolders = newFolders;
      this.deletePaths = deletePaths;
    },
    submit() {
      this.contentError = validateSkillContent(this.localItem.content);
      if (this.contentError) return;

      this.$emit('submit', {
        skill: {
          displayDescription: this.localItem.displayDescription,
          content: this.localItem.content,
          tags: this.localItem.tags,
          organizations: this.localItem.organizations,
          models: this.localItem.models,
          scope: 'global'
        },
        stagedFiles: this.stagedFiles,
        newFolders: this.newFolders,
        deletePaths: this.deletePaths
      });
    },
    onCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style lang="scss" scoped>
.create-or-edit-skill-form {
  width: 100%;
  height: 100%;
  background: var(--theme-surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .form-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .form-row {
    &:not(:last-child) {
      margin-bottom: 0.75rem;
    }

    .field-label {
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.025em;
      display: block;
      margin-bottom: 5px;

      .info-tooltip {
        cursor: help;
        margin-left: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 13px;
        height: 13px;
        border-radius: 50%;
        border: 1px solid currentColor;
        font-size: 9px;
        font-style: normal;
        opacity: 0.45;
        vertical-align: middle;
        color: var(--theme-on-surface);
      }
    }

    .char-counter {
      font-size: 0.7rem;
      text-align: right;
      color: var(--theme-on-surface);
      opacity: 0.6;

      &.warn {
        color: var(--theme-warning, orange);
        opacity: 1;
      }
    }
  }

  .skill-name-label {
    display: flex;
    flex-direction: column;

    .skill-name-static {
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--theme-primary);
      padding: 0.25rem 0;
    }
  }

  .markdown-editor {
    height: 300px;
    border: 1px solid var(--theme-on-background-dark);
    border-radius: 4px;
    overflow: hidden;

    .code-editor {
      height: 100%;
    }
  }

  .content-error {
    margin-top: 0.25rem;
  }

  .button-container {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 2rem;
    background: var(--theme-surface);
    border-top: 1px solid var(--theme-on-background-dark);
  }

  .error {
    font-size: 0.8rem;
    color: var(--theme-error);
    text-align: left;
    padding: 0.25rem 0;
  }
}
</style>
